import { textColorFields } from '../_shared/textColor.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { animationFields, mediaRadiusField } from '../_shared/universal.js';
/**
 * custom-columns — Shrine Pro **Multicolumn** (`sections/multicolumn.liquid`) at config-by-config parity.
 *
 * REBUILD NOTE (2026-06): the previous version of this file modelled the WRONG Shrine section — "Custom
 * columns V2", an open 12-track grid whose columns were arbitrary surfaces (own bg/border/shadow/radius/
 * padding) each carrying one of ~24 leaf types (button, divider, rating, icon-with-text…). Shrine's
 * **Multicolumn** is the opposite: a UNIFORM card grid/slider where every column is structurally
 * `{ media, title, text, link }` and EVERY style knob lives at the SECTION level. This rebuild collapses the
 * per-column surface/leaf model into one fixed card shape and lifts all styling to the section, so the row is
 * uniform by construction (the founder's complaint was the old non-uniform "ça n'a aucun sens" render).
 *
 * CONFIG-BY-CONFIG PARITY — IMPLEMENTED (Shrine setting → ours):
 *  - Section General: heading (`title`) + `headingSize` (+custom desktop/mobile px) + `headingAlignment`,
 *    an optional `button`
 *    (label+link), the color_scheme cluster with a `custom` value that reveals the full background / gradient
 *    / image / text / button custom-color overrides, and the 14-pattern background engine + all its knobs.
 *  - Column cards: `cardsColorScheme` (+custom bg/gradient/heading/text), `cardsScope` (chrome wraps the
 *    whole column OR only the image tile — reference what's-in-box anatomy), `cardsCornerRadius`,
 *    `stretchCards` (equal heights) + `stretchedCardsContentAlignment`.
 *  - Columns media: `imageRatio` (adapt/portrait/square/circle) + `imageFocalPoint` (which edge the crop
 *    keeps), `mediaPosition` (top/bottom). The media always spans the column — the old `imageWidth`
 *    (third/half/full) is GONE: its inset ate the fraction it promised, and a photo shrunk inside its own
 *    column read as disfigured (founder, 15/09). The shape is `imageRatio`'s job.
 *  - Column content: `columnAlignment`, desktop/mobile heading + text sizes, desktop/mobile heading↔text gap.
 *  - Content containers: desktop/mobile vertical + horizontal padding.
 *  - Desktop: `columnsDesktop` (1-10), `desktopSpacing` (grid gap), `desktopFullPage`, `sliderDesktop` (LIVE
 *    — the row becomes a scroll-snap rail showing `columnsDesktop` cards, with `perMoveDesktop` +
 *    `desktopSidePadding` + arrow/dot positions).
 *  - Mobile: `columnsMobile` (1/2), `mobileSliderEnabled` (LIVE, + `mobilePreview` and its own arrow/dot
 *    positions). The legacy `sliderMobile` key is DEAD — read nowhere, kept only in old blobs.
 *  - Section padding top/bottom (Shrine default 36, mobile = 75%).
 *  - Column block: `image`, `title`, `text` (richtext), `link` (Shrine `link_label`+`link` → our LinkValue).
 *
 * DEFERRED — justified (Shopify-platform / theme-JS only, per the project rule):
 *  - Splide's AUTOPLAY / loop / adaptive height. The carousel itself is no longer deferred: `sliderDesktop`
 *    and `mobileSliderEnabled` drive the shared grid↔scroll-snap engine (`gridSliderCss` + `initSlider`), the same
 *    one featured-collection and testimonials use — arrows and dots included, swipe working with no JS.
 *  - Heading word-highlight clusters 1 & 2 (theme-JS richtext word-wrapping decoration).
 *  - Shopify-object settings: `display_id` (#shopify-section helper) and the section-level Add-to-Cart
 *    (`atc_*`, needs the Shopify product/cart objects).
 *
 * SHIPPED SINCE (2026-09-18) — the column `video` slot + its `muted_autoplay`, which the note below used to
 * list as missing. The document model grew a `video` field type in the meantime, so a column now holds EITHER
 * a clip or a photo (the clip wins when both are set, and the photo becomes its poster). The playback mode is
 * per COLUMN, not per section — the founder's ask: one column can loop a silent clip while the one beside it
 * waits for a click. The two modes need opposite treatments and the section renders them as such (see
 * `Component.tsx`): muted autoplay hands the clip to the shared deferred engine (`preload="none"` +
 * `<source data-src>`, zero bytes until it is on screen), click-to-play keeps a REAL `src` with native
 * controls, because a `data-src` would make the browser's own play button do nothing.
 */
const G_GENERAL = 'General';
const G_CARDS = 'Column cards';
const G_MEDIA = 'Columns media';
const G_CONTENT = 'Column content';
const G_CONTAINERS = 'Content containers';
const G_DESKTOP = 'Desktop';
/** Arrows + dots — shared by the desktop and the mobile slider, so they get their own group (Shrine). */
const G_SLIDER = 'Slider';
const G_MOBILE = 'Mobile';
const G_SECTION = 'Section padding';
const B_MEDIA = 'Media';
const B_CONTENT = 'Content';
/** Visibility select (Shrine `visibility` / "Display on"). */
const visibilityField = {
    id: 'visibility',
    type: 'select',
    label: 'Display on',
    group: G_GENERAL,
    default: 'always-display',
    options: [
        { value: 'always-display', label: 'All devices' },
        { value: 'desktop-hidden', label: 'Mobile only' },
        { value: 'mobile-hidden', label: 'Desktop only' },
    ],
};
/** The pattern-engine fields (Shrine pattern_bg cluster) — kept verbatim; `patternCss` already ports all 14. */
const patternFields = [
    {
        id: 'patternBg',
        type: 'select',
        label: 'Background pattern',
        group: G_GENERAL,
        default: 'none',
        options: [
            { value: 'none', label: 'None' },
            { value: 'dotted', label: 'Dots' },
            { value: 'grid', label: 'Grid' },
            { value: 'curved-grid', label: 'Curved grid' },
            { value: 'diagonal-stripes', label: 'Stripes' },
            { value: 'zigzag', label: 'ZigZag' },
            { value: 'arrow-pattern', label: '3D Boxes' },
            { value: 'radial-rings', label: 'Rings' },
            { value: 'diamond-grid-1', label: 'Diamond grid 1' },
            { value: 'diamond-grid-2', label: 'Diamond grid 2' },
            { value: 'quatrefoils', label: 'Quatrefoils' },
            { value: 'ovals', label: 'Ovals' },
            { value: 'hexagons', label: 'Hexagons' },
            { value: 'curvy', label: 'Curvy' },
        ],
    },
    {
        id: 'patternColor',
        type: 'color',
        label: 'Pattern color',
        group: G_GENERAL,
        default: { ref: 'theme', token: 'accent' },
        visibleWhen: { field: 'patternBg', notEquals: 'none' },
    },
    {
        id: 'patternSize',
        type: 'range',
        label: 'Pattern size / spacing',
        group: G_GENERAL,
        min: 4,
        max: 80,
        step: 2,
        unit: 'px',
        default: 20,
        visibleWhen: { field: 'patternBg', notEquals: 'none' },
    },
    {
        id: 'patternThickness',
        type: 'range',
        label: 'Line / dot thickness',
        group: G_GENERAL,
        min: 1,
        max: 10,
        step: 1,
        unit: 'px',
        default: 2,
        visibleWhen: {
            field: 'patternBg',
            equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
        },
    },
    {
        id: 'patternAngle',
        type: 'range',
        label: 'Stripe angle',
        group: G_GENERAL,
        min: 0,
        max: 180,
        step: 15,
        unit: 'deg',
        default: 45,
        visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
    },
    {
        id: 'patternOpacityMode',
        type: 'select',
        label: 'Opacity type',
        group: G_GENERAL,
        default: 'low',
        options: [
            { value: 'low', label: 'Low' },
            { value: 'normal', label: 'Normal' },
        ],
        visibleWhen: { field: 'patternBg', notEquals: 'none' },
    },
    {
        id: 'patternOpacityLow',
        type: 'range',
        label: 'Opacity',
        group: G_GENERAL,
        min: 1,
        max: 20,
        step: 1,
        unit: '%',
        default: 7,
        visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
    },
    {
        id: 'patternOpacityNormal',
        type: 'range',
        label: 'Opacity',
        group: G_GENERAL,
        min: 20,
        max: 100,
        step: 5,
        unit: '%',
        default: 80,
        visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
    },
];
/**
 * Section custom-color override cluster (Shrine `color_scheme == custom`). Gated on the `colorScheme` select's
 * `custom` value (NOT a separate checkbox — `custom` is one of the scheme's own values, matching Shrine).
 */
const customColorFields = [
    {
        id: 'customColorsBackground',
        type: 'color',
        label: 'Custom background color',
        group: G_GENERAL,
        default: { ref: 'custom', value: '#FFFFFF' },
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    {
        id: 'customGradientBackground',
        type: 'gradient',
        label: 'Custom gradient background',
        group: G_GENERAL,
        info: 'If added, replaces the background color when applicable.',
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    {
        id: 'customImageBackground',
        type: 'image',
        label: 'Custom image background',
        group: G_GENERAL,
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    {
        id: 'customMobileImageBackground',
        type: 'image',
        label: 'Custom mobile image background',
        group: G_GENERAL,
        info: 'If empty, the custom image background is also used on mobile.',
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    {
        id: 'customColorsText',
        type: 'color',
        label: 'Custom text color',
        group: G_GENERAL,
        default: { ref: 'custom', value: '#2E2A39' },
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    {
        id: 'customColorsSolidButtonBackground',
        type: 'color',
        label: 'Custom solid button background',
        group: G_GENERAL,
        default: { ref: 'custom', value: '#dd1d1d' },
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
    {
        id: 'customColorsSolidButtonText',
        type: 'color',
        label: 'Custom solid button label color',
        group: G_GENERAL,
        default: { ref: 'custom', value: '#ffffff' },
        visibleWhen: { field: 'colorScheme', equals: 'custom' },
    },
];
/** Column cards (the uniformity engine — all card chrome is section-level, applied identically to every card). */
const cardFields = [
    {
        id: 'cardsColorScheme',
        type: 'select',
        label: 'Cards color scheme',
        group: G_CARDS,
        default: 'bg-overlay',
        options: [
            { value: 'bg-overlay', label: 'Subtle overlay' },
            { value: 'surface', label: 'Surface' },
            { value: 'inverse', label: 'Inverse' },
            { value: 'none', label: 'None (plain, no card)' },
            { value: 'custom', label: 'Custom' },
        ],
    },
    {
        // Reference "what's in the box" anatomy: the card chrome wraps ONLY the image tile; heading and
        // text sit below it on the section background. Tiles stay perfectly equal (the image ratio sets
        // their height) no matter how many lines each caption wraps to.
        id: 'cardsScope',
        type: 'select',
        label: 'Card wraps',
        group: G_CARDS,
        default: 'column',
        options: [
            { value: 'column', label: 'Whole column (image + text)' },
            { value: 'media', label: 'Image only (text below the card)' },
        ],
        visibleWhen: { field: 'cardsColorScheme', notEquals: 'none' },
    },
    {
        id: 'customCardsColorsBackground',
        type: 'color',
        label: 'Custom background color',
        group: G_CARDS,
        default: { ref: 'custom', value: '#F3F3F3' },
        visibleWhen: { field: 'cardsColorScheme', equals: 'custom' },
    },
    {
        id: 'customCardsGradientBackground',
        type: 'gradient',
        label: 'Custom gradient background',
        group: G_CARDS,
        info: 'If added, replaces the background color when applicable.',
        visibleWhen: { field: 'cardsColorScheme', equals: 'custom' },
    },
    {
        id: 'customCardsColorsHeading',
        type: 'color',
        label: 'Custom headings color',
        group: G_CARDS,
        default: { ref: 'custom', value: '#222126' },
        visibleWhen: { field: 'cardsColorScheme', equals: 'custom' },
    },
    {
        id: 'customCardsColorsText',
        type: 'color',
        label: 'Custom text color',
        group: G_CARDS,
        default: { ref: 'custom', value: '#2E2A39' },
        visibleWhen: { field: 'cardsColorScheme', equals: 'custom' },
    },
    {
        id: 'cardsCornerRadius',
        type: 'range',
        label: 'Corner radius',
        group: G_CARDS,
        min: 0,
        max: 60,
        step: 2,
        unit: 'px',
        default: 20,
    },
    {
        id: 'stretchCards',
        type: 'checkbox',
        label: 'Make all cards same height',
        group: G_CARDS,
        default: false,
    },
    {
        id: 'stretchedCardsContentAlignment',
        type: 'select',
        label: 'Vertical alignment of stretched cards',
        group: G_CARDS,
        default: 'center',
        options: [
            { value: 'flex-start', label: 'Top' },
            { value: 'center', label: 'Center' },
            { value: 'flex-end', label: 'Bottom' },
        ],
        visibleWhen: { field: 'stretchCards', equals: true },
    },
];
/** Columns media (uniform across every card). */
const mediaFields = [
    {
        id: 'imageRatio',
        type: 'select',
        label: 'Media ratio',
        group: G_MEDIA,
        default: 'square',
        info: 'Every ratio except "Adapt to media" CROPS the photo to fill the frame. "Adapt to media" keeps it whole.',
        options: [
            { value: 'adapt', label: 'Adapt to media' },
            { value: 'portrait', label: 'Portrait' },
            { value: 'square', label: 'Square' },
            { value: 'circle', label: 'Circle' },
        ],
    },
    {
        // `object-position` of that crop. `center` is what the section has always done, so no existing section
        // moves — this is the knob that was missing when a portrait lost its head in a square frame (founder,
        // 15/09; measured: 20–25 % of the height cut, and the subject sits at the TOP of a standing shot).
        id: 'imageFocalPoint',
        type: 'select',
        label: 'Crop focal point',
        group: G_MEDIA,
        default: 'center',
        visibleWhen: { field: 'imageRatio', equals: ['portrait', 'square', 'circle'] },
        options: [
            { value: 'top', label: 'Top' },
            { value: 'center', label: 'Center' },
            { value: 'bottom', label: 'Bottom' },
        ],
    },
    {
        id: 'mediaPosition',
        type: 'select',
        label: 'Media position',
        group: G_MEDIA,
        default: 'top',
        options: [
            { value: 'top', label: 'Top' },
            { value: 'bottom', label: 'Bottom' },
        ],
    },
];
/** Column content (uniform typography for every card). */
const contentFields = [
    {
        id: 'columnAlignment',
        type: 'select',
        label: 'Text alignment',
        group: G_CONTENT,
        default: 'center',
        options: [
            { value: 'left', label: 'Left' },
            { value: 'center', label: 'Center' },
            { value: 'right', label: 'Right' },
        ],
    },
    {
        id: 'desktopHeadingSize',
        type: 'range',
        label: 'Desktop headings size',
        group: G_CONTENT,
        min: 14,
        max: 36,
        step: 2,
        unit: 'px',
        default: 24,
    },
    {
        id: 'mobileHeadingSize',
        type: 'range',
        label: 'Mobile headings size',
        group: G_CONTENT,
        min: 12,
        max: 32,
        step: 1,
        unit: 'px',
        default: 22,
    },
    {
        id: 'desktopTextSize',
        type: 'range',
        label: 'Desktop text size',
        group: G_CONTENT,
        min: 12,
        max: 24,
        step: 1,
        unit: 'px',
        default: 16,
    },
    {
        id: 'mobileTextSize',
        type: 'range',
        label: 'Mobile text size',
        group: G_CONTENT,
        min: 8,
        max: 22,
        step: 1,
        unit: 'px',
        default: 15,
    },
    {
        id: 'desktopTextTopMargin',
        type: 'range',
        label: 'Desktop spacing between the heading & text',
        group: G_CONTENT,
        min: 0,
        max: 30,
        step: 2,
        unit: 'px',
        default: 10,
    },
    {
        id: 'mobileTextTopMargin',
        type: 'range',
        label: 'Mobile spacing between the heading & text',
        group: G_CONTENT,
        min: 0,
        max: 30,
        step: 2,
        unit: 'px',
        default: 10,
    },
];
/** Column content containers (inner card padding). */
const containerFields = [
    {
        id: 'desktopContainerPaddingY',
        type: 'range',
        label: 'Desktop vertical padding',
        group: G_CONTAINERS,
        min: 0,
        max: 60,
        step: 2,
        unit: 'px',
        default: 24,
    },
    {
        id: 'desktopContainerPaddingX',
        type: 'range',
        label: 'Desktop horizontal padding',
        group: G_CONTAINERS,
        min: 0,
        max: 60,
        step: 2,
        unit: 'px',
        default: 24,
    },
    {
        id: 'mobileContainerPaddingY',
        type: 'range',
        label: 'Mobile vertical padding',
        group: G_CONTAINERS,
        min: 0,
        max: 50,
        step: 2,
        unit: 'px',
        default: 24,
    },
    {
        id: 'mobileContainerPaddingX',
        type: 'range',
        label: 'Mobile horizontal padding',
        group: G_CONTAINERS,
        min: 0,
        max: 50,
        step: 2,
        unit: 'px',
        default: 24,
    },
];
export const customColumnsSchema = {
    type: 'custom-columns',
    name: 'Multicolumn',
    // No `migrate` on purpose. THE LESSON (15/09, found by the founder: her mobile-carousel toggle switched
    // back by itself): `migrate` re-runs on EVERY render until the stored instance's version changes, and
    // nothing stamps that version forward — not the canvas, not autosave. So a migration that writes a field
    // the seller can also edit overwrites her click on the very next paint. v4 introduced the live mobile
    // switch under a NEW id (`mobileSliderEnabled`) instead of rewriting the old inert `sliderMobile`.
    version: 4,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── General ──────────────────────────────────────────────────────────────
        visibilityField,
        {
            id: 'title',
            type: 'text',
            label: 'Heading',
            group: G_GENERAL,
            default: 'Pourquoi nous choisir',
            defaultKey: 'section.why_choose_us',
            // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
            aiWritable: true,
            maxLength: 160,
        },
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: G_GENERAL,
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom desktop size',
            group: G_GENERAL,
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom mobile size',
            group: G_GENERAL,
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            // The heading was hard-centred until 15/09 (founder: "on doit pouvoir personnaliser le titre").
            // Same id + values as collapsible-content / faq, so it reads translated everywhere for free.
            id: 'headingAlignment',
            type: 'select',
            label: 'Heading alignment',
            group: G_GENERAL,
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'button',
            type: 'link',
            label: 'Button',
            group: G_GENERAL,
            info: 'Optional button shown under the columns (label + link).',
        },
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: G_GENERAL,
            default: 'light',
            options: [
                { value: 'light', label: 'Background 1' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'accent', label: 'Accent' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        ...customColorFields,
        // ── Text colors ────────────────────────────────────────────────────────────
        // Paint the words themselves — the section heading and the column headings take `heading*`, the column
        // paragraphs take `text*`. Both start unset, so an untouched section keeps painting from its colour
        // scheme (and from `customCardsColorsHeading` when the cards carry a custom scheme).
        ...textColorFields(),
        ...patternFields,
        // ── Column cards ───────────────────────────────────────────────────────────
        ...cardFields,
        // ── Columns media ──────────────────────────────────────────────────────────
        ...mediaFields,
        // ── Column content ─────────────────────────────────────────────────────────
        ...contentFields,
        // ── Content containers ─────────────────────────────────────────────────────
        ...containerFields,
        // ── Desktop ────────────────────────────────────────────────────────────────
        {
            id: 'columnsDesktop',
            type: 'range',
            label: 'Columns desktop',
            group: G_DESKTOP,
            min: 1,
            max: 10,
            step: 1,
            default: 3,
        },
        {
            id: 'desktopSpacing',
            type: 'range',
            label: 'Spacing',
            group: G_DESKTOP,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 40,
        },
        {
            id: 'desktopFullPage',
            type: 'checkbox',
            label: 'Full page width',
            group: G_DESKTOP,
            default: false,
        },
        {
            id: 'sliderDesktop',
            type: 'checkbox',
            label: 'Enable desktop slider',
            group: G_DESKTOP,
            default: false,
            info: 'Off: extra columns wrap onto a new row. On: the row scrolls sideways — "Columns, desktop" is how many show at once.',
        },
        {
            id: 'perMoveDesktop',
            type: 'range',
            label: 'Slides to scroll per move',
            group: G_DESKTOP,
            min: 1,
            max: 10,
            step: 1,
            default: 1,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopSidePadding',
            type: 'range',
            label: 'Preview of prev & next slide',
            group: G_DESKTOP,
            min: 0,
            max: 150,
            step: 5,
            unit: 'px',
            default: 0,
            visibleWhen: { field: 'sliderDesktop', equals: true },
        },
        {
            id: 'desktopArrows',
            type: 'select',
            label: 'Arrows position (desktop)',
            group: G_DESKTOP,
            default: 'sides',
            visibleWhen: { field: 'sliderDesktop', equals: true },
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On sides of the slider' },
            ],
        },
        {
            id: 'desktopDots',
            type: 'select',
            label: 'Dots position (desktop)',
            group: G_DESKTOP,
            default: 'under',
            visibleWhen: { field: 'sliderDesktop', equals: true },
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
        },
        // ── Mobile ─────────────────────────────────────────────────────────────────
        {
            id: 'columnsMobile',
            type: 'select',
            label: 'Columns mobile',
            group: G_MOBILE,
            default: '1',
            options: [
                { value: '1', label: '1 column' },
                { value: '2', label: '2 columns' },
            ],
        },
        {
            // NOT `sliderMobile`: that id was stored-but-inert for months and the preset shipped it `true`, so
            // its stored value means nothing. A migration that cleared it would have to run on EVERY render
            // (a stored instance keeps its version forever — nothing stamps it forward), which would undo the
            // seller's own click each time. A NEW id starts everyone at "off" and stays editable.
            id: 'mobileSliderEnabled',
            type: 'checkbox',
            label: 'Enable mobile slider',
            group: G_MOBILE,
            default: false,
            info: 'Off: the columns stack. On: they scroll sideways, one swipe per column.',
        },
        {
            id: 'mobilePreview',
            type: 'checkbox',
            label: 'Preview of prev & next slides',
            group: G_MOBILE,
            default: false,
            visibleWhen: { field: 'mobileSliderEnabled', equals: true },
        },
        {
            id: 'mobileArrows',
            type: 'select',
            label: 'Arrows position (mobile)',
            group: G_MOBILE,
            default: 'under',
            visibleWhen: { field: 'mobileSliderEnabled', equals: true },
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
                { value: 'sides', label: 'On sides of the slider' },
            ],
        },
        {
            id: 'mobileDots',
            type: 'select',
            label: 'Dots position (mobile)',
            group: G_MOBILE,
            default: 'under',
            visibleWhen: { field: 'mobileSliderEnabled', equals: true },
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slider' },
            ],
        },
        // ── Slider controls (arrows + dots colours, shared by both breakpoints) ────
        {
            id: 'transparentArrows',
            type: 'checkbox',
            label: 'Transparent arrows',
            group: G_SLIDER,
            default: true,
        },
        {
            id: 'arrowsScheme',
            type: 'select',
            label: 'Arrows color',
            group: G_SLIDER,
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        // The five schemes are PAIRS derived from the band's own colours; `custom` is the escape hatch for a
        // seller who wants the control to read against a background photo or a pattern, where no derived pair
        // is right. Two fields, because a solid arrow has a disc AND a glyph — one colour for both would be a
        // circle you cannot see the ‹ in. The glyph field hides while the arrows are transparent: there is no
        // disc then, and the single colour already paints the border and the glyph together.
        {
            id: 'arrowsCustomColor',
            type: 'color',
            label: 'Custom arrows color',
            group: G_SLIDER,
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'arrowsScheme', equals: 'custom' },
        },
        {
            id: 'arrowsCustomIconColor',
            type: 'color',
            label: 'Custom arrows icon color',
            group: G_SLIDER,
            default: { ref: 'theme', token: 'bg' },
            visibleWhen: [
                { field: 'arrowsScheme', equals: 'custom' },
                { field: 'transparentArrows', equals: false },
            ],
        },
        {
            id: 'dotsScheme',
            type: 'select',
            label: 'Dots color',
            group: G_SLIDER,
            default: 'inverse',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'dotsCustomColor',
            type: 'color',
            label: 'Custom dots color',
            group: G_SLIDER,
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'dotsScheme', equals: 'custom' },
        },
        {
            id: 'sliderControlsAlign',
            type: 'select',
            label: 'Controls alignment',
            group: G_SLIDER,
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
            info: 'Where the arrows and dots UNDER the slider sit. Arrows placed on the sides keep hugging the edges.',
        },
        mediaRadiusField,
        // The universal Animation group. Spread rather than re-declared: this section writes its own colour
        // scheme and padding by hand, which is exactly why it was the one section in the inspector with no
        // Animation group at all until 2026-09-18.
        ...animationFields,
        // ── Section padding ────────────────────────────────────────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: G_SECTION,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: G_SECTION,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
    ],
    // Each COLUMN is a uniform card: ONE media slot + heading + text + link. Zero per-column styling
    // (all chrome is section-level), so the row is uniform by construction.
    blocks: [
        {
            type: 'column',
            name: 'Column',
            max: 12,
            fields: [
                { id: 'image', type: 'image', label: 'Image', group: B_MEDIA },
                {
                    id: 'video',
                    type: 'video',
                    label: 'Video',
                    group: B_MEDIA,
                    // Uploaded clip, never a pasted URL (project rule) — the control only takes an upload/drag/paste.
                    info: 'Shown instead of the image. Use a short MP4/WebM rather than a GIF; the image above is kept as its poster.',
                },
                {
                    id: 'videoMutedAutoplay',
                    type: 'checkbox',
                    label: 'Mute & autoplay video',
                    group: B_MEDIA,
                    default: true,
                    // `empty: false`, NOT `notEquals: ''`: a video value is an OBJECT ({ sources, poster }), and the
                    // inspector's `comparable()` collapses richtext and picker refs to a scalar but not a clip — so
                    // `notEquals: ''` would compare an object against a string and hold for EVERY column, shown or not.
                    // Clearing the control stores `undefined`, which `isEmptyValue` reads correctly.
                    visibleWhen: { field: 'video', empty: false },
                },
                playButtonField({
                    defaultOn: false,
                    group: B_MEDIA,
                    visibleWhen: [
                        { field: 'video', empty: false },
                        { field: 'videoMutedAutoplay', equals: true },
                    ],
                }),
                {
                    id: 'title',
                    type: 'text',
                    label: 'Heading',
                    group: B_CONTENT,
                    // Descriptive placeholder (the page-builder convention): clearly "replace me", readable, professional
                    // — NOT lorem-ipsum gibberish and NOT final-looking marketing copy. FR to match the store.
                    default: 'Titre de la colonne',
                    defaultKey: 'section.column_title',
                    // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
                    aiWritable: true,
                    maxLength: 160,
                },
                {
                    id: 'text',
                    type: 'richtext',
                    label: 'Text',
                    group: B_CONTENT,
                    default: {
                        format: 'html',
                        html: '<p>Décrivez ici un avantage, un service ou une garantie qui rassure vos clients et les incite à passer commande.</p>',
                    },
                },
                {
                    id: 'link',
                    type: 'link',
                    label: 'Link',
                    group: B_CONTENT,
                    info: 'Optional link shown under the text (label + URL).',
                },
            ],
        },
    ],
    maxBlocks: 12,
};
export const customColumnsPresets = [
    {
        name: 'Multicolumn',
        settings: {
            visibility: 'always-display',
            title: '@t:section.why_choose_us',
            headingSize: 'h1',
            headingAlignment: 'center',
            colorScheme: 'light',
            patternBg: 'none',
            cardsColorScheme: 'bg-overlay',
            cardsCornerRadius: 20,
            stretchCards: true,
            stretchedCardsContentAlignment: 'flex-start',
            imageRatio: 'square',
            mediaPosition: 'top',
            columnAlignment: 'center',
            desktopHeadingSize: 24,
            mobileHeadingSize: 22,
            desktopTextSize: 16,
            mobileTextSize: 15,
            desktopTextTopMargin: 10,
            mobileTextTopMargin: 10,
            desktopContainerPaddingY: 24,
            desktopContainerPaddingX: 24,
            mobileContainerPaddingY: 24,
            mobileContainerPaddingX: 24,
            columnsDesktop: 3,
            desktopSpacing: 40,
            desktopFullPage: false,
            sliderDesktop: false,
            columnsMobile: '1',
            mobileSliderEnabled: false,
            paddingTop: 36,
            paddingBottom: 36,
        },
        // Three identical PLACEHOLDER columns (heading + text + link, NO image). This IS the "add component"
        // default state: a clean, obviously-editable template — not a pre-filled marketing demo, not lorem ipsum.
        // The seller fills in real content + adds images (the media box appears only once an image is set).
        blocks: [
            {
                type: 'column',
                settings: {
                    title: '@t:section.column_title',
                    text: {
                        format: 'html',
                        html: '<p>Décrivez ici un avantage, un service ou une garantie qui rassure vos clients et les incite à passer commande.</p>',
                    },
                    link: { type: 'external', url: '', label: 'En savoir plus' },
                },
            },
            {
                type: 'column',
                settings: {
                    title: '@t:section.column_title',
                    text: {
                        format: 'html',
                        html: '<p>Décrivez ici un avantage, un service ou une garantie qui rassure vos clients et les incite à passer commande.</p>',
                    },
                    link: { type: 'external', url: '', label: 'En savoir plus' },
                },
            },
            {
                type: 'column',
                settings: {
                    title: '@t:section.column_title',
                    text: {
                        format: 'html',
                        html: '<p>Décrivez ici un avantage, un service ou une garantie qui rassure vos clients et les incite à passer commande.</p>',
                    },
                    link: { type: 'external', url: '', label: 'En savoir plus' },
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const customColumnsMeta = {
    label: 'Multicolumn',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map